import { openDB } from 'idb';

/**
 * MY1-2830: client-side cache for the split wallet payload (shell + benefits),
 * backed by IndexedDB so it can hold the multi-MB payload localStorage can't.
 *
 * Stale-while-revalidate: the wallet page hydrates instantly from here, then
 * revalidates over the network and overwrites the cache.
 *
 * Per-user scoping (NOT just per-browser): IndexedDB is origin-scoped and shared
 * by every account that signs into this browser profile, so records are keyed +
 * ownership-verified by the user UUID, an owner-change wipe runs before hydrate,
 * and clearAll() runs on logout. A different user can never read another's data.
 */
const DB_NAME = 'my10x-wallet-cache';
const STORE = 'wallet';

// Bump to invalidate every cached payload after a shape change. Doubles as the
// IndexedDB version, so a bump drops and recreates the store (see upgrade()).
const SCHEMA_VERSION = 1;

// Past this age a cached payload is treated as a miss (cold load). Younger data
// is hydrated for instant paint regardless — the network revalidation corrects it.
const HARD_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

// localStorage marker for the last user that wrote to the cache on this browser.
const OWNER_KEY = 'wallet_cache_owner';

let dbPromise = null;

function idbAvailable() {
    return typeof indexedDB !== 'undefined';
}

function getDb() {
    if (!idbAvailable()) {
        return Promise.resolve(null);
    }

    if (!dbPromise) {
        dbPromise = openDB(DB_NAME, SCHEMA_VERSION, {
            upgrade(db) {
                // Any schema bump bumps the DB version: drop and recreate the
                // store so records written under an older shape are unreadable.
                if (db.objectStoreNames.contains(STORE)) {
                    db.deleteObjectStore(STORE);
                }
                db.createObjectStore(STORE, { keyPath: 'key' });
            },
        }).catch((e) => {
            console.warn('[walletCache] IndexedDB unavailable:', e);
            return null;
        });
    }

    return dbPromise;
}

function readOwner() {
    try {
        return localStorage.getItem(OWNER_KEY);
    } catch (e) {
        return null;
    }
}

function writeOwner(userUuid) {
    try {
        localStorage.setItem(OWNER_KEY, userUuid);
    } catch (e) {
        // ignore — private browsing / storage disabled
    }
}

/**
 * @param {string|null} userUuid The authenticated user's UUID. Required for the
 *   per-user read/write/invalidate ops; clearAll() works without it.
 */
export function useWalletCache(userUuid = null) {
    const keyFor = (section) => `${userUuid}:${section}:${SCHEMA_VERSION}`;

    async function readSection(section) {
        if (!userUuid) {
            return null;
        }
        try {
            const db = await getDb();
            if (!db) {
                return null;
            }
            const record = await db.get(STORE, keyFor(section));
            if (!record) {
                return null;
            }
            // Defensive ownership / schema / freshness guards.
            if (record.userUuid !== userUuid) {
                return null;
            }
            if (record.schemaVersion !== SCHEMA_VERSION) {
                return null;
            }
            if (Date.now() - (record.savedAt || 0) > HARD_TTL_MS) {
                return null;
            }
            return record;
        } catch (e) {
            console.warn(`[walletCache] read ${section} failed:`, e);
            return null;
        }
    }

    async function writeSection(section, payload) {
        if (!userUuid || !payload) {
            return;
        }
        try {
            const db = await getDb();
            if (!db) {
                return;
            }
            await db.put(STORE, {
                key: keyFor(section),
                userUuid,
                walletId: payload?.wallet?.id ?? null,
                section,
                schemaVersion: SCHEMA_VERSION,
                savedAt: Date.now(),
                payload,
            });
            writeOwner(userUuid);
        } catch (e) {
            // QuotaExceededError, serialization issues, etc. — non-fatal.
            console.warn(`[walletCache] write ${section} failed:`, e);
        }
    }

    return {
        /**
         * Wipe the whole cache when the signed-in user changed on this browser.
         * Runs before hydrate so a different account never reads stale data.
         */
        async ensureOwner() {
            if (!userUuid) {
                return;
            }
            const previous = readOwner();
            if (previous && previous !== userUuid) {
                await this.clearAll();
            }
            writeOwner(userUuid);
        },

        /**
         * @returns {Promise<{shell: object, benefits: object|null}|null>} the raw
         *   API payloads, or null on a miss (no shell cached / not this user / too old).
         */
        async hydrate() {
            await this.ensureOwner();
            const [shellRec, benefitsRec] = await Promise.all([
                readSection('shell'),
                readSection('benefits'),
            ]);
            if (!shellRec) {
                return null;
            }
            return {
                shell: shellRec.payload,
                benefits: benefitsRec?.payload ?? null,
                // Age of the cached shell, so callers can skip a redundant
                // revalidation when the data is only seconds old (MY1-2830).
                ageMs: Date.now() - (shellRec.savedAt ?? 0),
            };
        },

        saveShell(payload) {
            return writeSection('shell', payload);
        },

        saveBenefits(payload) {
            return writeSection('benefits', payload);
        },

        /** Drop this user's cached sections (e.g. before a post-mutation refetch). */
        async invalidate() {
            if (!userUuid) {
                return;
            }
            try {
                const db = await getDb();
                if (!db) {
                    return;
                }
                await Promise.all([
                    db.delete(STORE, keyFor('shell')),
                    db.delete(STORE, keyFor('benefits')),
                ]);
            } catch (e) {
                console.warn('[walletCache] invalidate failed:', e);
            }
        },

        /** Wipe every user's wallet cache on this browser (logout / owner change). */
        async clearAll() {
            try {
                const db = await getDb();
                if (!db) {
                    return;
                }
                await db.clear(STORE);
            } catch (e) {
                console.warn('[walletCache] clearAll failed:', e);
            }
        },
    };
}
